import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, BarChart, Shield, TrendingUp, Calendar, Radio, Plane, UserCheck,
  ArrowRight, Check, ChevronRight, Star, Target, Award, Briefcase,
  Globe, Building, Heart, Zap
} from 'lucide-react';
import MegaMenu from '../../components/public/MegaMenu';
import Footer from '../../components/public/Footer';

// Service Data Configuration
const SERVICES_DATA = {
  'sports-agency': {
    title: 'Sports Agency',
    subtitle: 'Talent Management & Player Representation',
    description: 'Comprehensive talent management services helping athletes build successful careers through strategic representation, contract negotiation, and brand development across Africa.',
    icon: Users,
    color: 'from-green-600 to-green-700',
    heroImage: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=1200&h=600&fit=crop',
    capabilities: [
      { title: 'Player Representation', description: 'Contract negotiation and career management for professional athletes' },
      { title: 'Talent Scouting', description: 'Identifying and developing emerging sports talent across Africa' },
      { title: 'Brand Development', description: 'Building athlete personal brands and endorsement opportunities' },
      { title: 'Career Transition', description: 'Supporting athletes with post-career planning and opportunities' },
      { title: 'Legal Support', description: 'Expert legal guidance on contracts, transfers, and disputes' },
      { title: 'Financial Planning', description: 'Wealth management and financial advisory for athletes' }
    ],
    caseStudies: [
      {
        title: 'Rising Star Program',
        client: 'Kenya Athletics Federation',
        result: '25 athletes signed to international clubs',
        description: 'Developed a comprehensive scouting and development program that identified and placed promising Kenyan athletes with top international clubs.'
      },
      {
        title: 'Brand Partnership Success',
        client: 'Professional Footballer',
        result: '$2M in endorsements secured',
        description: 'Managed the complete brand strategy for a top African footballer, resulting in partnerships with major global brands.'
      }
    ],
    stats: [
      { value: '150+', label: 'Athletes Represented' },
      { value: '$15M+', label: 'Contracts Negotiated' },
      { value: '30+', label: 'International Placements' },
      { value: '12', label: 'Sports Covered' }
    ]
  },
  'sports-technology': {
    title: 'Sports Technology',
    subtitle: 'Digital Platforms & Innovation',
    description: 'Cutting-edge digital solutions designed specifically for African sports organizations, including membership management, tournament systems, and analytics platforms.',
    icon: BarChart,
    color: 'from-blue-600 to-blue-700',
    heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=600&fit=crop',
    capabilities: [
      { title: 'Platform Development', description: 'Custom sports management platforms and mobile applications' },
      { title: 'MyGolfHub', description: 'Africa\'s premier golf management and handicap tracking system' },
      { title: 'League Management', description: 'Comprehensive tournament and league administration systems' },
      { title: 'Data Analytics', description: 'Performance tracking and business intelligence solutions' },
      { title: 'Digital Transformation', description: 'Modernizing sports organizations with technology' },
      { title: 'Integration Services', description: 'Connecting systems for seamless operations' }
    ],
    caseStudies: [
      {
        title: 'MyGolfHub Implementation',
        client: 'Kenya Golf Union',
        result: '15,000+ golfers onboarded',
        description: 'Deployed our flagship golf management platform across 50+ clubs in Kenya, revolutionizing handicap tracking and tournament management.'
      },
      {
        title: 'Federation Digitization',
        client: 'National Football Federation',
        result: '60% operational efficiency gain',
        description: 'Implemented a complete digital transformation including player registration, licensing, and league management.'
      }
    ],
    stats: [
      { value: '50+', label: 'Platforms Deployed' },
      { value: '100K+', label: 'Users Managed' },
      { value: '99.9%', label: 'System Uptime' },
      { value: '8', label: 'Countries Served' }
    ],
    featured: {
      title: 'MyGolfHub',
      description: 'Our flagship golf management platform',
      link: '/mygolfhub'
    }
  },
  'governance-advisory': {
    title: 'Governance & Advisory',
    subtitle: 'Strategic Sports Consulting',
    description: 'Expert consulting services for sports bodies, government agencies, and organizations seeking to establish best practices in sports administration and governance.',
    icon: Shield,
    color: 'from-purple-600 to-purple-700',
    heroImage: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&h=600&fit=crop',
    capabilities: [
      { title: 'Governance Frameworks', description: 'Developing policies, constitutions, and operational structures' },
      { title: 'Compliance & Licensing', description: 'Club licensing systems and regulatory compliance programs' },
      { title: 'Strategic Planning', description: 'Long-term vision and roadmap development for sports bodies' },
      { title: 'Capacity Building', description: 'Training administrators and building organizational capabilities' },
      { title: 'Audit & Assessment', description: 'Governance audits and organizational health checks' },
      { title: 'Stakeholder Management', description: 'Building relationships between government, federations, and clubs' }
    ],
    caseStudies: [
      {
        title: 'Club Licensing System',
        client: 'Football Kenya Federation',
        result: '100% club compliance achieved',
        description: 'Designed and implemented a comprehensive club licensing criteria system aligned with CAF and FIFA standards.'
      },
      {
        title: 'Federation Restructuring',
        client: 'National Sports Council',
        result: 'New governance framework adopted',
        description: 'Led a complete organizational restructuring including new constitution, policies, and operational procedures.'
      }
    ],
    stats: [
      { value: '25+', label: 'Federations Advised' },
      { value: '100+', label: 'Policies Developed' },
      { value: '500+', label: 'Officials Trained' },
      { value: '15', label: 'Countries Reached' }
    ]
  },
  'commercial-sponsorship': {
    title: 'Commercial & Sponsorship',
    subtitle: 'Revenue Generation & Brand Partnerships',
    description: 'Driving commercial success for sports organizations through sponsorship acquisition, brand partnerships, and innovative revenue generation strategies.',
    icon: TrendingUp,
    color: 'from-red-600 to-red-700',
    heroImage: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=1200&h=600&fit=crop',
    capabilities: [
      { title: 'Sponsorship Acquisition', description: 'Identifying and securing sponsors for events and organizations' },
      { title: 'Rights Valuation', description: 'Comprehensive valuation of sponsorship and media rights' },
      { title: 'Brand Activation', description: 'Creating engaging sponsorship activation campaigns' },
      { title: 'Partnership Management', description: 'Managing sponsor relationships and deliverables' },
      { title: 'Revenue Strategy', description: 'Developing sustainable commercial revenue streams' },
      { title: 'Market Research', description: 'Sports market analysis and opportunity identification' }
    ],
    caseStudies: [
      {
        title: 'Multi-Year Title Sponsorship',
        client: 'Premier Football League',
        result: '$5M sponsorship secured',
        description: 'Negotiated a landmark multi-year title sponsorship deal with a leading telecommunications company.'
      },
      {
        title: 'Sports Tourism Partnership',
        client: 'Golf Tourism Board',
        result: '200% increase in bookings',
        description: 'Developed a strategic partnership between golf clubs and tourism agencies, creating integrated golf tourism packages.'
      }
    ],
    stats: [
      { value: '$50M+', label: 'Revenue Generated' },
      { value: '100+', label: 'Sponsorships Secured' },
      { value: '75+', label: 'Brand Partners' },
      { value: '40%', label: 'Avg. Revenue Growth' }
    ]
  },
  'events-operations': {
    title: 'Events & Operations',
    subtitle: 'Tournament & Event Management',
    description: 'End-to-end event management for sports tournaments, championships, and corporate sports programs, delivering world-class experiences across Africa.',
    icon: Calendar,
    color: 'from-orange-600 to-orange-700',
    heroImage: 'https://images.unsplash.com/photo-1540747913346-19e32dc3e97e?w=1200&h=600&fit=crop',
    capabilities: [
      { title: 'Tournament Management', description: 'Complete planning and execution of sports tournaments' },
      { title: 'Event Production', description: 'Professional event production and stage management' },
      { title: 'Logistics Coordination', description: 'Venue, accommodation, and transport management' },
      { title: 'Technical Operations', description: 'Scoring systems, timing, and results management' },
      { title: 'Broadcast Support', description: 'Production support for live event broadcasting' },
      { title: 'Hospitality Services', description: 'VIP hosting and corporate hospitality programs' }
    ],
    caseStudies: [
      {
        title: 'Road to SomaBay',
        client: 'Michezonet Golf',
        result: '500+ participants annually',
        description: 'Created and managed Kenya\'s premier amateur golf circuit with qualifying events leading to an international finale in Egypt.',
        link: '/somabay'
      },
      {
        title: 'National Championship',
        client: 'Sports Federation',
        result: '10,000+ attendees',
        description: 'Managed all operational aspects of a major national championship including venue, logistics, and broadcast coordination.'
      }
    ],
    stats: [
      { value: '200+', label: 'Events Delivered' },
      { value: '50K+', label: 'Participants Managed' },
      { value: '98%', label: 'Client Satisfaction' },
      { value: '15', label: 'Countries' }
    ],
    featured: {
      title: 'Road to SomaBay',
      description: 'Kenya\'s premier amateur golf circuit',
      link: '/somabay'
    }
  },
  'media-communications': {
    title: 'Media & Communications',
    subtitle: 'Sports Media & PR',
    description: 'Comprehensive media services including public relations, content production, broadcasting support, and digital communications for sports organizations.',
    icon: Radio,
    color: 'from-pink-600 to-pink-700',
    heroImage: 'https://images.unsplash.com/photo-1478720568477-152d9b164e26?w=1200&h=600&fit=crop',
    capabilities: [
      { title: 'Public Relations', description: 'Media relations, press management, and reputation building' },
      { title: 'Content Production', description: 'Video, photography, and written content creation' },
      { title: 'Broadcasting Rights', description: 'Media rights negotiation and distribution' },
      { title: 'Digital Marketing', description: 'Social media management and digital campaigns' },
      { title: 'Crisis Communications', description: 'Reputation management and crisis response' },
      { title: 'Brand Storytelling', description: 'Creating compelling narratives for sports brands' }
    ],
    caseStudies: [
      {
        title: 'Social Media Campaign',
        client: 'National Team',
        result: '500% engagement increase',
        description: 'Developed and executed a comprehensive social media strategy that dramatically increased fan engagement and following.'
      },
      {
        title: 'Broadcasting Partnership',
        client: 'Sports League',
        result: 'TV coverage in 10 countries',
        description: 'Negotiated broadcasting deals that brought league coverage to multiple African countries.'
      }
    ],
    stats: [
      { value: '1M+', label: 'Content Views' },
      { value: '100+', label: 'Media Partners' },
      { value: '50+', label: 'Campaigns Delivered' },
      { value: '25', label: 'Broadcasting Deals' }
    ]
  },
  'sports-tourism': {
    title: 'Sports Tourism',
    subtitle: 'Golf Tourism & Travel Experiences',
    description: 'Curating exceptional sports travel experiences including golf tourism packages, sports event travel, and destination experiences across Africa and beyond.',
    icon: Plane,
    color: 'from-cyan-600 to-cyan-700',
    heroImage: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=1200&h=600&fit=crop',
    capabilities: [
      { title: 'Golf Tourism Packages', description: 'Curated golf holidays and tournament travel experiences' },
      { title: 'Destination Management', description: 'Complete travel planning and local coordination' },
      { title: 'Group Travel', description: 'Corporate and team travel arrangements' },
      { title: 'Event Travel', description: 'Travel packages for major sporting events' },
      { title: 'Accommodation Partners', description: 'Premium hotel and resort partnerships' },
      { title: 'Experience Curation', description: 'Unique sports and cultural experiences' }
    ],
    caseStudies: [
      {
        title: 'SomaBay Golf Experience',
        client: 'Road to SomaBay Qualifiers',
        result: '100+ travelers annually',
        description: 'Coordinated complete travel packages for Kenyan golfers to compete in Egypt, including flights, accommodation, and tournament logistics.',
        link: '/somabay'
      },
      {
        title: 'Corporate Golf Retreat',
        client: 'Multinational Corporation',
        result: '50 executives hosted',
        description: 'Organized an exclusive corporate golf retreat combining business networking with premium golf experiences.'
      }
    ],
    stats: [
      { value: '500+', label: 'Travelers Served' },
      { value: '20+', label: 'Destinations' },
      { value: '50+', label: 'Partner Hotels' },
      { value: '100%', label: 'Trip Success Rate' }
    ]
  },
  'workforce-solutions': {
    title: 'Workforce Solutions',
    subtitle: 'Sports Industry Recruitment & Training',
    description: 'Building capacity in Africa\'s sports industry through professional recruitment, training programs, and workforce development initiatives.',
    icon: UserCheck,
    color: 'from-amber-600 to-amber-700',
    heroImage: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&h=600&fit=crop',
    capabilities: [
      { title: 'Executive Search', description: 'Recruiting senior leadership for sports organizations' },
      { title: 'Staff Recruitment', description: 'Finding qualified professionals for sports industry roles' },
      { title: 'Training Programs', description: 'Capacity building and professional development courses' },
      { title: 'Internship Programs', description: 'Developing the next generation of sports professionals' },
      { title: 'Volunteer Management', description: 'Building and managing volunteer networks for events' },
      { title: 'HR Consulting', description: 'Organizational structure and HR strategy advice' }
    ],
    caseStudies: [
      {
        title: 'Federation Leadership Program',
        client: 'Multiple Sports Federations',
        result: '50+ leaders trained',
        description: 'Developed and delivered a comprehensive leadership training program for sports administrators.'
      },
      {
        title: 'Event Workforce',
        client: 'International Sports Event',
        result: '200 volunteers recruited',
        description: 'Recruited, trained, and managed a volunteer workforce for a major international sporting event.'
      }
    ],
    stats: [
      { value: '200+', label: 'Placements Made' },
      { value: '500+', label: 'Professionals Trained' },
      { value: '50+', label: 'Organizations Served' },
      { value: '95%', label: 'Placement Success' }
    ]
  }
};

const ServiceDetailPage = () => {
  const { serviceSlug } = useParams();
  const service = SERVICES_DATA[serviceSlug];

  if (!service) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-heading font-bold text-white mb-4">Service Not Found</h1>
          <Link to="/services" className="text-[#008000] hover:underline">Back to Services</Link>
        </div>
      </div>
    );
  }

  const IconComponent = service.icon;

  return (
    <div className="min-h-screen bg-black">
      <MegaMenu />
      
      {/* Hero Section */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={service.heroImage}
            alt={service.title}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Link 
              to="/services" 
              className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
            >
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back to Services
            </Link>
            
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center`}>
                <IconComponent className="w-8 h-8 text-white" />
              </div>
              <div>
                <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">{service.subtitle}</span>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-white">
                  {service.title}
                </h1>
              </div>
            </div>
            
            <p className="text-white/70 text-xl max-w-3xl mb-10 leading-relaxed">
              {service.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#008000] hover:bg-[#006600] text-white font-semibold rounded-lg transition-all shadow-lg shadow-[#008000]/25"
              >
                Request Proposal
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact#consultation"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg transition-all border border-white/20"
              >
                Book Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-gradient-to-r from-[#008000]/20 to-transparent border-y border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {service.stats.map((stat) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <p className="text-3xl md:text-4xl font-heading font-bold text-[#008000]">{stat.value}</p>
                <p className="text-white/60 text-sm mt-1">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">What We Offer</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-4">
              Our Capabilities
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {service.capabilities.map((capability, idx) => (
              <motion.div
                key={capability.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-xl p-6 hover:border-[#008000]/50 transition-all"
              >
                <div className="w-10 h-10 rounded-lg bg-[#008000]/20 flex items-center justify-center mb-4">
                  <Check className="w-5 h-5 text-[#008000]" />
                </div>
                <h3 className="text-white font-heading font-bold text-lg mb-2">{capability.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{capability.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Project (if exists) */}
      {service.featured && (
        <section className="py-16 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link 
              to={service.featured.link}
              className="block bg-gradient-to-r from-[#008000]/20 to-transparent border border-[#008000]/30 rounded-2xl p-8 hover:border-[#008000]/50 transition-all group"
            >
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-[#00CC00] text-sm font-medium uppercase tracking-wider">Featured</span>
                  <h3 className="text-2xl md:text-3xl font-heading font-bold text-white mt-2">{service.featured.title}</h3>
                  <p className="text-white/60 mt-2">{service.featured.description}</p>
                </div>
                <ArrowRight className="w-8 h-8 text-[#008000] group-hover:translate-x-2 transition-transform" />
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Case Studies */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-[#008000] font-medium text-sm uppercase tracking-wider">Success Stories</span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-4">
              Case Studies
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {service.caseStudies.map((study, idx) => (
              <motion.div
                key={study.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white/5 border border-white/10 rounded-xl p-8 hover:border-[#008000]/50 transition-all"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Award className="w-5 h-5 text-[#008000]" />
                  <span className="text-[#00CC00] text-sm font-medium">{study.result}</span>
                </div>
                <h3 className="text-xl font-heading font-bold text-white mb-2">{study.title}</h3>
                <p className="text-white/50 text-sm mb-4">{study.client}</p>
                <p className="text-white/70 leading-relaxed">{study.description}</p>
                {study.link && (
                  <Link 
                    to={study.link}
                    className="inline-flex items-center gap-2 text-[#008000] hover:text-[#00CC00] mt-4 text-sm font-medium transition-colors"
                  >
                    Learn More <ArrowRight size={14} />
                  </Link>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#008000] to-[#006600] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
              Let's discuss how our {service.title.toLowerCase()} services can help your organization succeed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#008000] font-semibold rounded-lg hover:bg-gray-100 transition-colors"
              >
                Request Proposal
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent border-2 border-white text-white font-semibold rounded-lg hover:bg-white/10 transition-colors"
              >
                View All Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceDetailPage;
